import React, { useContext, useState } from 'react'

import { Account } from '@driftt/api-types'
import { Box, Button, Flex, Header, Icon, IconButton, Link, Tabs } from '@driftt/dds'
import { faLinkedin } from '@driftt/dds.icons/brands/faLinkedin'
import { faArrowUpRightFromSquare } from '@driftt/dds.icons/light/faArrowUpRightFromSquare'
import { faXmark } from '@driftt/dds.icons/light/faXmark'

import { AccountActivityAccountAvatar } from 'components/Avatars'
import { CurrentUserContext } from 'context/currentUserContext'
import { useAccountDetails } from 'hooks/useAccountDetails'
import { CompanyData } from 'modules/accountActivity/domain'
import SocialLinkIconButtons from 'pages/CCRMAccountDetailsPage/AccountDetailsHeader/SocialLinkIconButton'
import { CurrentUser } from 'typings/currentUser'
import { accountPath } from 'utils/paths'
import { segmentTrack, TrackType } from 'utils/tracking'

import { TimelineEventsFeed } from '../ActivityFeed'
import ContactsTab from '../ContactsTab'
import DrawerOpportunitiesTab from '../DrawerOpportunitiesTab'
import OverviewTab from '../OverviewTab'

const LINKEDIN_URL = 'https://www.linkedin.com/'

interface CompanyInfoProps {
  accountData: Account | undefined
  companyData: CompanyData | undefined
  inCrm: boolean
}

const CompanyInfo: React.FC<CompanyInfoProps> = ({ accountData, companyData, inCrm }) => {
  const accountDomain = accountData?.domain
  const companyDomain = companyData?.domain
  return (
    <Flex>
      <AccountActivityAccountAvatar imageUrl={companyData?.logo} size="medium" inCrm={inCrm} />
      <Box>
        <Header variant="h3">
          {accountData?.name || companyData?.name || companyData?.domain}
        </Header>
        <Link target="_blank" href={`https://www.${companyDomain || accountDomain}`}>
          {`https://www.${companyDomain || accountDomain}`}
        </Link>
      </Box>
    </Flex>
  )
}

interface DrawerActionsProps {
  accountId: string
  linkedinHandle: string | undefined
  salesforceData: any
  setIsOpen: (isOpen: boolean) => void
  trackAction: (action: string) => void
  currentUser: CurrentUser | undefined
}

const DrawerActions: React.FC<DrawerActionsProps> = ({
  accountId,
  linkedinHandle,
  salesforceData,
  setIsOpen,
  trackAction,
  currentUser
}) => {
  const salesforceAccount = salesforceData?.accounts ? salesforceData?.accounts[0] : {}

  return (
    <Flex>
      <SocialLinkIconButtons
        validation={!!linkedinHandle}
        trackType={TrackType.ACCOUNT_DETAILS_HEADER_NAVIGATES_TO_LINKEDIN}
        href={`${LINKEDIN_URL}${linkedinHandle?.toLocaleLowerCase()}`}
        tooltip="LinkedIn"
        icon={faLinkedin}
        trackAttrs={{
          userId: currentUser?.id,
          orgId: currentUser?.orgId,
          action: 'LinkedIn'
        }}
      />
      {salesforceData?.baseUrl && salesforceAccount && (
        <Button
          variant="secondary"
          onClick={() => {
            window.open(
              `${salesforceData?.baseUrl}lightning/r/Account/${salesforceAccount?.Id}/view`,
              '_blank'
            )
            trackAction('View in CRM')
          }}>
          <Icon icon={faArrowUpRightFromSquare} title={'close'} size="small" />
          View in CRM
        </Button>
      )}
      <Button
        variant="secondary"
        onClick={() => {
          window.open(accountPath(accountId), '_blank')
          trackAction('Open in Drift')
        }}>
        View in Drift
      </Button>
      <IconButton icon={faXmark} title={'close'} onClick={() => setIsOpen(false)} />
    </Flex>
  )
}

interface DrawerHeaderProps {
  accountId: string
  setIsOpen: (isOpen: boolean) => void
}

const DrawerHeader: React.FC<DrawerHeaderProps> = ({ accountId, setIsOpen }) => {
  const currentUser = useContext(CurrentUserContext)
  const trackAction = (action: string, additionalData?: any) => {
    segmentTrack(TrackType.DRAWER_ACTION, {
      userId: currentUser?.id,
      orgId: currentUser?.orgId,
      action,
      page: 'AccountDrawer',
      ...additionalData
    })
  }

  const [tabValue, setTabValue] = useState('overview')
  const {
    hasSfdc,
    isLoading,
    accountData,
    companyData,
    salesforceData,
    explainData,
    opportunities,
    opportunitiesError,
    accountFitScore,
    accountScoreBreakdown,
    topPagesAndTopics
  } = useAccountDetails(accountId)

  const numOfOpportunities =
    hasSfdc && accountData && opportunities ? (
      <Link
        variant="inline"
        css={{ fontWeight: 400 }}
        onClick={() => {
          setTabValue('Opportunities')
          trackAction(`Opportunities click`, { source: 'account info' })
        }}>
        {opportunities?.length}
      </Link>
    ) : (
      undefined
    )

  const navOptions = [
    {
      label: 'Overview',
      value: 'overview',
      content: (
        <OverviewTab
          accountId={accountId}
          accountData={accountData}
          companyData={companyData}
          explainData={explainData}
          accountFitScore={accountFitScore}
          numOfOpportunities={numOfOpportunities}
          topPagesAndTopics={topPagesAndTopics}
          isLoading={isLoading}
          trackAction={trackAction}
          scoreBreakdown={accountScoreBreakdown}
        />
      )
    },
    {
      label: 'Activity',
      value: 'Activity',
      content: (
        <TimelineEventsFeed accountId={accountId} trackAction={trackAction} inDrawer={true} />
      )
    },
    {
      label: 'Contacts',
      value: 'Contacts',
      content: <ContactsTab accountId={accountId} trackAction={trackAction} />
    },
    {
      label: 'Opportunities',
      value: 'Opportunities',
      content: (
        <DrawerOpportunitiesTab
          accountId={accountId}
          salesforceData={salesforceData}
          opportunities={opportunities}
          opportunitiesError={opportunitiesError}
        />
      )
    }
  ]

  return (
    <Flex
      direction="column"
      fullWidth
      fullHeight
      css={{
        '& > :last-child': {
          minHeight: 'calc(100% - 88px)'
        },
        //tabs styles
        '& > :last-child > :first-child': {
          padding: '0px 16px 0px 16px',
          marginBottom: '0px'
        }
      }}>
      <Flex justifyContent="space-between" fullWidth css={{ padding: '16px' }}>
        <CompanyInfo
          accountData={accountData}
          companyData={companyData}
          inCrm={!!explainData?.account}
        />
        <DrawerActions
          accountId={accountId}
          linkedinHandle={companyData?.linkedinHandle}
          salesforceData={salesforceData}
          setIsOpen={setIsOpen}
          trackAction={trackAction}
          currentUser={currentUser}
        />
      </Flex>
      <Tabs
        value={tabValue}
        options={navOptions}
        onValueChange={value => {
          setTabValue(value)
          trackAction(`${value} click`, { source: 'Tab' })
        }}
      />
    </Flex>
  )
}

export default DrawerHeader
